import React, { useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { Zip } from '@root/components/checkout/product_pages/steps/smart_storage/zip';
import { StorageCheckoutStep } from '@root/components/checkout/types';

import {
  BrandLink,
  Card,
  OutlineButton,
  SectionIntro,
  SectionStack,
  SectionTitle,
} from '../shared';
import { CHALLENGER as T } from '../tokens';

/** Matches `useBreakpoints().isDesktop` (MD_INT = 840). */
const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

/** Figma 707:10049 — column, gap 8, items-start so Learn more stays left. */
const IntroBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[8]};
  padding-bottom: ${T.space[8]};
`;

/**
 * Figma 707:10058 / 707:20058 — Medium 16/24 text-primary.
 * Div (not h3) + flex strut matches Figma `leading-[0]` / justify-center text box.
 */
const LocationTitle = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};
`;

/**
 * Mobile card (Figma 707:20055):
 * - Top row: title + ZIP (col gap 4) | Change — items-start, gap 4.
 * - Pricing note is full card width below the row (Figma note sits under the
 *   38px button and is wider than the left column, so it must not wrap in the
 *   narrow flex column).
 */
const MobileLocation = styled.div`
  display: none;
  width: 100%;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${T.space[4]};
  }
`;

const MobileTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${T.space[4]};
  width: 100%;
`;

/** Figma 707:20057 — column gap 4; line-height 0 like Figma leading-[0]. */
const MobileCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[4]};
  line-height: 0;
`;

/** Figma 707:20060 — ZIP line; pt 2 only (note is full-width below the row). */
const MobileZipLine = styled.div`
  padding-top: 2px;
  font-family: ${T.font.sans};
  font-size: 14px;
  line-height: 21px;
  color: ${T.color.textTertiary};
`;

const ZipLabel = styled.span`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

const ZipValue = styled.span`
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textTertiary};
`;

/** Figma 707:20061 — Regular 13/18 text-tertiary; full width under Change. */
const PricingNote = styled.div`
  margin: 0;
  font-family: ${T.font.sans};
  font-size: 13px;
  font-weight: ${FontWeight.Regular};
  line-height: 18px;
  color: ${T.color.textTertiary};
`;

/**
 * Figma 707:20062 — outline Change; Demi Bold 12/14 text-primary on mobile.
 */
const ChangeButton = styled(OutlineButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  box-sizing: border-box;
  color: ${T.color.textPrimary};
`;

/**
 * Desktop (707:10056): title + inline ZIP • note + Change on one row.
 */
const DesktopLocation = styled.div`
  display: none;
  align-items: center;
  gap: ${T.space[4]};
  width: 100%;

  ${DESKTOP} {
    display: flex;
  }
`;

const DesktopCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[4]};
  font-family: ${T.font.sans};
`;

const MetaRow = styled.div`
  padding: 2px 0;
  font-family: ${T.font.sans};
  white-space: nowrap;
  line-height: 21px;

  > * + * {
    margin-left: ${T.space[4]};
  }
`;

const Dot = styled.span`
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
`;

const Note = styled.span`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

/** Figma 707:6434 — edit card padding pt24 / px24 / pb12. */
const EditCard = styled(Card)`
  padding: ${T.space[24]} ${T.space[24]} ${T.space[12]};
`;

/** Figma 707:6436 — column gap 16. */
const EditStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
`;

/** Figma 707:6437 — title + helper, gap 4. */
const EditHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[4]};
  width: 100%;
`;

/** Figma 707:6440 — Regular 14/20 text-tertiary. */
const EditNote = styled.p`
  margin: 0;
  padding: 2px 0;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

type Props = {
  onLearnMore: () => void;
};

export const StorageLocationSection: React.FC<Props> = ({ onLearnMore }) => {
  const {
    flowState: { values, getStepProps, onChange, goToStep },
  } = useStorageCheckoutContext();
  /** Collapsed “Change” view only for an already-validated ZIP on load. */
  const [editing, setEditing] = useState(!values.zipValidated);

  const stepProps = {
    ...getStepProps(),
    enabled: true,
    scrollToStep: () => undefined,
    onChange: ((key, value, cb) => {
      onChange(key, value, (...args) => {
        if (key === 'zipValidated' && value === true) {
          // Stay on the edit card so the success caption (894:16647) remains.
          goToStep(StorageCheckoutStep.PlanSize);
        }
        cb?.(...args);
      });
    }) as typeof onChange,
  };

  const onEdit = () => setEditing(true);

  return (
    <SectionStack>
      <IntroBlock>
        <SectionTitle>Storage plan</SectionTitle>
        <SectionIntro>
          Backed by Iron Mountain, the standard in secure storage for over 70
          years, and your belongings are protected the same way.
        </SectionIntro>
        <BrandLink type="button" onClick={onLearnMore}>
          Learn more
        </BrandLink>
      </IntroBlock>

      {editing ? (
        <EditCard>
          <EditStack>
            <EditHeader>
              <LocationTitle>Storage location</LocationTitle>
              <EditNote>
                Pricing and availability are based on entered location.
              </EditNote>
            </EditHeader>
            <Zip {...stepProps} appearance="challenger" />
          </EditStack>
        </EditCard>
      ) : (
        <Card>
          <MobileLocation>
            <MobileTop>
              <MobileCopy>
                <LocationTitle>Storage location</LocationTitle>
                <MobileZipLine>
                  <ZipLabel>ZIP code: </ZipLabel>
                  <ZipValue>{values.zip}</ZipValue>
                </MobileZipLine>
              </MobileCopy>
              <ChangeButton type="button" onClick={onEdit}>
                Change
              </ChangeButton>
            </MobileTop>
            <PricingNote>
              Pricing and availability are based on entered location.
            </PricingNote>
          </MobileLocation>

          <DesktopLocation>
            <DesktopCopy>
              <LocationTitle>Storage location</LocationTitle>
              <MetaRow>
                <ZipLabel>ZIP code:</ZipLabel>
                <ZipValue>{values.zip}</ZipValue>
                <Dot aria-hidden>•</Dot>
                <Note>
                  Pricing and availability are based on entered location.
                </Note>
              </MetaRow>
            </DesktopCopy>
            <ChangeButton type="button" onClick={onEdit}>
              Change
            </ChangeButton>
          </DesktopLocation>
        </Card>
      )}
    </SectionStack>
  );
};
